import React, { useEffect, useMemo, useState } from "react";
import { X, CheckCircle, XCircle, Warning, Clock } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { logError } from "@/lib/logger";
import { stageEnteredLabel } from "@/lib/stageHistory";

const CURRENCIES = [
    { code: "INR", symbol: "₹" },
    { code: "USD", symbol: "$" },
    { code: "GBP", symbol: "£" },
    { code: "EUR", symbol: "€" },
    { code: "AED", symbol: "د.إ" },
    { code: "SGD", symbol: "S$" },
];

/**
 * Records the sub-status outcome for the Offer or Hired stage.
 *
 * @param {"offer" | "hire"} kind
 * @param {string} candidateId
 * @param {object} existing  Prior outcome object (if any) to prefill
 */
export default function OfferHireOutcomeModal({ kind, candidateId, existing, stageHistory, onClose, onSaved }) {
    const isOffer = kind === "offer";
    const isHire = kind === "hire";
    const positiveKey = isOffer ? "accepted" : "hired";
    const negativeKey = isOffer ? "rejected" : "no_show";
    const positiveLabel = isOffer ? "Offer accepted" : "Hired · joined";
    const negativeLabel = isOffer ? "Offer rejected" : "No-show";
    const title = isOffer ? "Record offer outcome" : "Confirm joining";

    const [status, setStatus] = useState(existing?.status || positiveKey);
    const [joiningDate, setJoiningDate] = useState(existing?.joining_date || existing?.actual_joining_date || "");
    const [ctcAmount, setCtcAmount] = useState(existing?.ctc_amount || "");
    const [ctcCurrency, setCtcCurrency] = useState(existing?.ctc_currency || "INR");
    const [reason, setReason] = useState(existing?.reason || "");
    const [saving, setSaving] = useState(false);

    // Default the joining date to +14 days for a smoother recruiter flow.
    useEffect(() => {
        if (!joiningDate && status === positiveKey) {
            const d = new Date();
            d.setDate(d.getDate() + 14);
            setJoiningDate(d.toISOString().slice(0, 10));
        }
    }, [status, joiningDate, positiveKey]);

    const positive = status === positiveKey;

    const canSubmit = useMemo(() => {
        if (positive) {
            if (isOffer) {
                return !!joiningDate && Number(ctcAmount) > 0 && !!ctcCurrency;
            }
            return !!joiningDate;
        }
        return true; // negative outcome only needs (optional) reason
    }, [positive, isOffer, joiningDate, ctcAmount, ctcCurrency]);

    const submit = async (e) => {
        e.preventDefault();
        if (!canSubmit || saving) return;
        setSaving(true);
        try {
            const url = isOffer
                ? `/candidates/${candidateId}/offer-outcome`
                : `/candidates/${candidateId}/hire-outcome`;
            const body = { status };
            if (positive && isOffer) {
                body.joining_date = joiningDate;
                body.ctc_amount = Number(ctcAmount);
                body.ctc_currency = ctcCurrency;
            } else if (positive && isHire) {
                body.actual_joining_date = joiningDate;
            } else if (!positive) {
                body.reason = reason;
            }
            const r = await api.post(url, body);
            toast.success(
                positive
                    ? (isOffer ? "Offer accepted — joining date + CTC saved." : "Hire confirmed. 🎉")
                    : (isOffer ? "Offer rejected — candidate moved to Rejected." : "No-show recorded — candidate moved to Rejected."),
            );
            onSaved?.(r.data);
            onClose?.();
        } catch (err) {
            logError("outcome save failed", err);
            toast.error(err?.response?.data?.detail || "Couldn't save outcome.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div
            data-testid={`outcome-modal-${kind}`}
            className="fixed inset-0 z-[70] bg-ink/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
            onClick={onClose}
        >
            <div
                className="bg-white w-full sm:max-w-md max-h-[92vh] overflow-y-auto shadow-2xl"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between p-4 sm:p-5 border-b border-[#E5E5E5]">
                    <div>
                        <h2 className="font-heading text-xl sm:text-2xl font-black tracking-tight text-ink">
                            {title}
                        </h2>
                        {stageEnteredLabel(stageHistory, isOffer ? "Offer" : "Hired") && (
                            <p
                                data-testid="outcome-modal-entered-at"
                                className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#A3A3A3] mt-1 inline-flex items-center gap-1"
                            >
                                <Clock size={10} weight="bold" />
                                {stageEnteredLabel(stageHistory, isOffer ? "Offer" : "Hired")}
                            </p>
                        )}
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        data-testid="outcome-modal-close"
                        className="p-1 hover:bg-[#F5F5F5]"
                        aria-label="Close"
                    >
                        <X size={16} weight="bold" />
                    </button>
                </div>

                <form onSubmit={submit} className="p-4 sm:p-5 space-y-4">
                    {/* Status picker */}
                    <div className="grid grid-cols-2 gap-2">
                        <button
                            type="button"
                            data-testid={`outcome-choose-${positiveKey}`}
                            onClick={() => setStatus(positiveKey)}
                            className={`p-3 border text-left transition-colors ${
                                status === positiveKey
                                    ? "border-[#059669] bg-[#ECFDF5]"
                                    : "border-[#E5E5E5] hover:border-ink"
                            }`}
                        >
                            <div className="flex items-center gap-2">
                                <CheckCircle
                                    size={16}
                                    weight={status === positiveKey ? "fill" : "bold"}
                                    className="text-[#059669]"
                                />
                                <span className="font-black text-sm text-ink">{positiveLabel}</span>
                            </div>
                        </button>
                        <button
                            type="button"
                            data-testid={`outcome-choose-${negativeKey}`}
                            onClick={() => setStatus(negativeKey)}
                            className={`p-3 border text-left transition-colors ${
                                status === negativeKey
                                    ? "border-[#DC2626] bg-[#FEF2F2]"
                                    : "border-[#E5E5E5] hover:border-ink"
                            }`}
                        >
                            <div className="flex items-center gap-2">
                                <XCircle
                                    size={16}
                                    weight={status === negativeKey ? "fill" : "bold"}
                                    className="text-[#DC2626]"
                                />
                                <span className="font-black text-sm text-ink">{negativeLabel}</span>
                            </div>
                        </button>
                    </div>

                    {positive && (
                        <>
                            <div>
                                <label className="block text-[10px] font-black uppercase tracking-[0.15em] text-[#525252] mb-1">
                                    {isOffer ? "Expected joining date" : "Actual joining date"}
                                </label>
                                <input
                                    type="date"
                                    data-testid="outcome-joining-date"
                                    value={joiningDate}
                                    onChange={(e) => setJoiningDate(e.target.value)}
                                    className="w-full border border-[#E5E5E5] px-3 py-2.5 text-sm focus:outline-none focus:border-ink"
                                    required
                                />
                            </div>

                            {isOffer && (
                                <div>
                                    <label className="block text-[10px] font-black uppercase tracking-[0.15em] text-[#525252] mb-1">
                                        Offered CTC
                                    </label>
                                    <div className="flex gap-2">
                                        <select
                                            value={ctcCurrency}
                                            onChange={(e) => setCtcCurrency(e.target.value)}
                                            data-testid="outcome-ctc-currency"
                                            className="border border-[#E5E5E5] px-3 py-2.5 text-sm focus:outline-none focus:border-ink"
                                        >
                                            {CURRENCIES.map((c) => (
                                                <option key={c.code} value={c.code}>{c.code}</option>
                                            ))}
                                        </select>
                                        <input
                                            type="number"
                                            min="0"
                                            step="1"
                                            data-testid="outcome-ctc-amount"
                                            value={ctcAmount}
                                            onChange={(e) => setCtcAmount(e.target.value)}
                                            placeholder="e.g. 1800000"
                                            className="flex-1 border border-[#E5E5E5] px-3 py-2.5 text-sm focus:outline-none focus:border-ink"
                                            required
                                        />
                                    </div>
                                </div>
                            )}
                        </>
                    )}

                    {!positive && (
                        <>
                            <div className="flex items-start gap-2 border border-[#FEE2E2] bg-[#FEF2F2] p-3">
                                <Warning size={14} weight="fill" className="text-[#DC2626] mt-0.5 shrink-0" />
                                <p className="text-[11px] text-[#B91C1C] leading-relaxed">
                                    Recording {negativeLabel.toLowerCase()} will move the candidate
                                    to the <b>Rejected</b> stage. This can be reversed later by
                                    changing the stage manually.
                                </p>
                            </div>
                            <div>
                                <label className="block text-[10px] font-black uppercase tracking-[0.15em] text-[#525252] mb-1">
                                    Reason (optional, saved to history)
                                </label>
                                <textarea
                                    data-testid="outcome-reason"
                                    value={reason}
                                    onChange={(e) => setReason(e.target.value)}
                                    rows={2}
                                    placeholder={isOffer
                                        ? "e.g. counter-offer from current employer"
                                        : "e.g. stopped responding after joining date"}
                                    className="w-full border border-[#E5E5E5] px-3 py-2 text-sm focus:outline-none focus:border-ink resize-none"
                                />
                            </div>
                        </>
                    )}

                    <div className="flex gap-2 pt-2">
                        <button
                            type="button"
                            onClick={onClose}
                            className="flex-1 border border-[#E5E5E5] px-4 py-2.5 text-[11px] font-black uppercase tracking-wider text-[#525252] hover:border-ink hover:text-ink"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            data-testid="outcome-submit"
                            disabled={!canSubmit || saving}
                            className={`flex-1 px-4 py-2.5 text-[11px] font-black uppercase tracking-wider text-white ${
                                positive ? "bg-[#059669] hover:bg-[#047857]" : "bg-[#DC2626] hover:bg-[#B91C1C]"
                            } disabled:opacity-50 disabled:cursor-not-allowed`}
                        >
                            {saving ? "Saving…" : "Save outcome"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
